/**
 * @file IntegrationInstanceRow.tsx
 * @description Строка экземпляра интеграции на странице «Интеграции»
 * (phase19.29, второй проход: вынесена из страницы, подписи — словами
 * владельца).
 *
 * Слева — имя и «пилюли»: область действия («Вся платформа» / магазин),
 * «основная», статус, «ключи доступа»; под ними — последнее обращение и
 * текст ошибки. Справа — компактные кнопки-значки с подписями для программ
 * чтения с экрана: проверить подключение (зелёный — проверка), статистика,
 * удалить (красный). На телефоне кнопки уходят под текст, чтобы имени
 * хватило места. Клик по тексту открывает редактирование.
 */

import { ArrowRightLeft, BarChart3, ChevronUp, Globe, PlugZap, Store, Trash2 } from "lucide-react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { IntegrationUsagePanel } from "./IntegrationUsagePanel";
import { INTEGRATION_STATUS_META } from "./status-meta";
import type { PlatformIntegrationDto } from "../../lib/api-platform-integrations";

interface Props {
  item: PlatformIntegrationDto;
  expanded: boolean;
  testing: boolean;
  /** Идёт проверка другого экземпляра — проверку этого блокируем. */
  testBusy: boolean;
  onEdit: () => void;
  onTest: () => void;
  onToggleUsage: () => void;
  onDelete: () => void;
  /** «Перенести в магазин…» — только у общей интеграции и при правах (phase19.28). */
  onTransfer?: () => void;
}

export function IntegrationInstanceRow({
  item,
  expanded,
  testing,
  testBusy,
  onEdit,
  onTest,
  onToggleUsage,
  onDelete,
  onTransfer,
}: Props) {
  const status = INTEGRATION_STATUS_META[item.status];
  const usageLabel = expanded ? "Свернуть статистику" : "Показать статистику";
  return (
    <div>
      <div className="flex flex-col gap-2 rounded-md px-2 py-3 transition-colors hover:bg-surface-low/40 sm:flex-row sm:items-center">
        <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-on-surface">{item.name}</span>
            <Badge variant={item.shop ? "secondary" : "outline"}>
              {item.shop ? <Store className="size-3" /> : <Globe className="size-3" />}
              {item.shop ? item.shop.displayName : "Вся платформа"}
            </Badge>
            {item.isPrimary ? <Badge variant="primary">основная</Badge> : null}
            <Badge variant={status.variant}>{status.label}</Badge>
            {item.hasCredentials ? <Badge variant="outline">ключи доступа</Badge> : null}
          </div>
          <div className="mt-0.5 text-xs text-on-surface-variant">
            {item.lastSyncAt
              ? `Последнее обращение: ${new Date(item.lastSyncAt).toLocaleString("ru-RU")}`
              : "Обращений ещё не было"}
            {item.errorMessage ? ` · ${item.errorMessage}` : ""}
          </div>
        </button>
        <div className="flex shrink-0 items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            title="Проверить подключение"
            aria-label={`Проверить подключение «${item.name}»`}
            className="text-success hover:bg-success/10"
            loading={testing}
            disabled={!item.hasCredentials || testBusy}
            onClick={onTest}
          >
            <PlugZap className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            title={usageLabel}
            aria-label={`${usageLabel} «${item.name}»`}
            aria-expanded={expanded}
            onClick={onToggleUsage}
          >
            {expanded ? <ChevronUp className="size-4" /> : <BarChart3 className="size-4" />}
          </Button>
          {onTransfer && item.shopId === null ? (
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              title="Перенести в магазин…"
              aria-label={`Перенести «${item.name}» в магазин…`}
              onClick={onTransfer}
            >
              <ArrowRightLeft className="size-4" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            title="Удалить"
            aria-label={`Удалить «${item.name}»`}
            onClick={onDelete}
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
      {expanded ? (
        <div className="px-2 pb-4 pt-1">
          <IntegrationUsagePanel integration={item} />
        </div>
      ) : null}
    </div>
  );
}
